import type { FormatNumberOptions, IntlShape } from "@formatjs/intl";

export type NumberFormatFunction = (value: number) => string;

export function NumberFormatter(currency: string, intl: IntlShape) {
  const getNumberParts = (num: number, useCurrency: boolean = false) => {
    return intl
      .formatNumberToParts(
        12345.67,
        useCurrency ? { style: "currency", currency } : {},
      )
      .reduce<Record<string, string>>((acc, part) => {
        acc[part.type] = part.value;
        return acc;
      }, {});
  };
  const { currency: currencySymbol } = getNumberParts(12345.67, true);
  // The Intl object will format numbers and currencies differently
  // for some locales.  This was first encountered with en-DE.
  // Setting the group and decimal symbols based on the number format.
  const { group: groupSymbol, decimal: decimalSymbol } =
    getNumberParts(12345.67);
  const formatNumber = (options: FormatNumberOptions) => (value: number) => {
    return intl.formatNumber(value, options);
  };
  return {
    compactCurrency: formatNumber({
      currency,
      maximumSignificantDigits: 4,
      minimumFractionDigits: 0,
      notation: "compact",
      style: "currency",
    }),
    currency: formatNumber({
      currency,
      style: "currency",
    }),
    decimal: formatNumber({
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    }),
    decimalNoGrouping: formatNumber({
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
      useGrouping: false,
    }),
    float: formatNumber({}),
    floatNoGrouping: formatNumber({ useGrouping: false }),
    format: formatNumber,
    integer: formatNumber({
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    }),
    integerNoGrouping: formatNumber({
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
      useGrouping: false,
    }),
    integerCurrency: formatNumber({
      currency,
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
      style: "currency",
    }),
    percent: formatNumber({ style: "percent" }),
    currencySymbol,
    groupSymbol,
    decimalSymbol,
  };
}
export type NumberFormatter = ReturnType<typeof NumberFormatter>;
